import React, { useEffect, useState, useRef } from "react";
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import ProgressBar from "@uppy/progress-bar";
import { MdDeleteOutline } from "react-icons/md";
import { MdCheckCircle } from "react-icons/md";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import "@uppy/progress-bar/dist/style.css";
import { BlobServiceClient } from "@azure/storage-blob";
import { containerName, sasToken, storageAccountName } from "../../data/constant";

/**
 * Uploads a file to Azure Blob Storage and returns the file URL
 * @param {File} file - The file to upload
 * @returns {Promise<string>} The URL of the uploaded file
 */
async function uploadFileToBlob(file) {
  console.info("Uploading file to Azure Blob Storage");
  try {


    const blobServiceClient = new BlobServiceClient(
      `https://${storageAccountName}.blob.core.windows.net/?${sasToken}`
    );

    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(file.name);
    const fileBuffer = await file.arrayBuffer();
    const contentType = file.type;

    await blockBlobClient.upload(fileBuffer, fileBuffer.byteLength, {
      blobHTTPHeaders: { blobContentType: contentType },
    });

    const fileUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${file.name}`;
    return fileUrl;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
}

/**
 * Component for uploading and managing images
 */
function ImageUploader({
  allowedFileTypes = ["image/png", "image/jpeg"],
  onSuccess,
}) {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileURL, setUploadedFileURL] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const progressBarRef = useRef(null);

  // Initialize Uppy instance
  const uppy = React.useMemo(() => {
    console.info("Initializing Uppy instance");
    return new Uppy({
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes,
      },
      autoProceed: true,
    });
  }, [allowedFileTypes]);

  useEffect(() => {
    console.info("Setting up Uppy event listeners");
    
    if (!uppy.getPlugin("ProgressBar") && progressBarRef.current) {
      uppy.use(ProgressBar, {
        target: progressBarRef.current,
        hideAfterFinish: false,
      });
    }

    uppy.on("upload-progress", (file, progress) => {
      setUploadProgress(progress.progress);
    });

    // Handle file upload completion
    uppy.on("complete", async (result) => {
      console.info("Processing completed upload");
      if (result.successful.length > 0) {
        const uploadedFile = result.successful[0].data;
        setUploadedFile(uploadedFile.name);
        setUploading(true);
        try {
          const fileURL = await uploadFileToBlob(uploadedFile);

          const img = new Image();
          img.src = fileURL;
          img.onload = () => {
            const fileURLWithRatio = `${fileURL}?width=${img.naturalWidth}&height=${img.naturalHeight}`;
            setUploadedFileURL(fileURLWithRatio);
            if (onSuccess) {
              onSuccess(fileURLWithRatio);
            }
          };
          img.onerror = () => {
            setUploadedFileURL(fileURL);
            if (onSuccess) {
              onSuccess(fileURL);
            }
          };
        } catch (error) {
          console.error("Error during file upload:", error);
        } finally {
          setUploading(false);
        }
      }
    });
  }, [uppy, onSuccess]);

  /**
   * Handles deletion of uploaded file
   */
  const handleDelete = () => {
    console.info("Deleting uploaded file");
    setUploadedFile(null);
    setUploadedFileURL(null);
    setUploadProgress(0);
  };

  return (
    <div className="mx-auto flex flex-col gap-4">
      {/* Uppy Dashboard */}
      {!uploading && !uploadedFile && (
        <Dashboard
          uppy={uppy}
          height={190}
          hideUploadButton
          proudlyDisplayPoweredByUppy={false}
        />
      )}
      

      {/* Progress Bar */}
      {uploading && (
        <div className="w-full border p-4 -mt-4 text-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-2">
            {uploadedFile}
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        </div>
      )}
      <div ref={progressBarRef} className="uppy-progress"></div>

      {!uploading && !uploadedFile && (
        <p className="text-gray-500 text-sm mb-2">
          Supported format: PNG, JPEG
        </p>
      )}

      {/* Display Uploaded File */}
      {uploadedFile && !uploading && (
        <div className="border p-4 -mt-4 mb-3 text-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-2 relative">
            <div className="relative h-10 w-10">
              <img
                src={uploadedFileURL}
                alt="Uploaded"
                className="h-full w-full object-cover rounded"
              />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <MdCheckCircle className="text-green-500 text-xl" />
              </div>
            </div>
            {uploadedFile}
          </div>
          <div onClick={handleDelete}>
            <MdDeleteOutline className="text-xl cursor-pointer" />
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageUploader;
